import { useMemo, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  EditableSlotGroup,
  RichTextContent,
  useCollectionBinding,
  useSlotGroup,
} from '@vibesites/runtime/react';

// ============================================================================
// Types
// ============================================================================

interface FAQItem {
  question?: string | null;
  answer?: string | null;
  category?: string | null;
}

interface FAQGroup {
  category: string;
  /** True when this group was created for items with no explicit category —
      we bucket them together but skip the "General" heading render so pages
      without real categories don't show a placeholder title. */
  isImplicit: boolean;
  items: Array<{ item: FAQItem; index: number }>;
}

// ============================================================================
// Schema definitions (module-level literals — extractor follows one level)
// ============================================================================

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Contact CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/contact-us',
      suggestedFields: ['property_website_url'],
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Contact Us',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

// ============================================================================
// Helpers
// ============================================================================

// Multihub sometimes stores an unset category as a stringified falsy value.
const PLACEHOLDER_CATEGORIES = new Set(['false', 'true', 'null', 'undefined', 'none']);

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const isTitleLine = (line: string) => line.length <= 90 && !/[.!?:]$/.test(line);

/**
 * Multihub FAQ answers are usually plain text where structure lives in line
 * breaks: "- item" lists, "Title\nDescription" pairs, and "Label: value"
 * lines. Answers that already contain HTML pass through untouched.
 */
function formatAnswer(answer: string): string {
  if (/<[a-z][\s\S]*>/i.test(answer)) return answer;

  const out: string[] = [];
  let listItems: string[] = [];
  const flushList = () => {
    if (listItems.length) out.push(`<ul>${listItems.map((li) => `<li>${li}</li>`).join('')}</ul>`);
    listItems = [];
  };

  const blocks = answer
    .split(/\n\s*\n/)
    .map((b) => b.split('\n').map((l) => l.trim()).filter(Boolean))
    .filter((b) => b.length > 0);

  for (const lines of blocks) {
    if (lines.every((l) => /^[-*•]\s+/.test(l))) {
      listItems.push(...lines.map((l) => escapeHtml(l.replace(/^[-*•]\s+/, ''))));
      continue;
    }
    if (lines.length === 2 && isTitleLine(lines[0]) && !isTitleLine(lines[1])) {
      listItems.push(`<strong>${escapeHtml(lines[0])}</strong><br />${escapeHtml(lines[1])}`);
      continue;
    }
    flushList();
    for (const line of lines) {
      const labelled = line.match(/^([^:]{1,40}):\s+(.+)$/);
      if (labelled) {
        listItems.push(`<strong>${escapeHtml(labelled[1])}:</strong> ${escapeHtml(labelled[2])}`);
        continue;
      }
      flushList();
      out.push(
        lines.length > 1 && isTitleLine(line)
          ? `<p><strong>${escapeHtml(line)}</strong></p>`
          : `<p>${escapeHtml(line)}</p>`,
      );
    }
  }
  flushList();
  return out.join('');
}

function groupByCategory(items: FAQItem[]): FAQGroup[] {
  const groups = new Map<string, FAQGroup>();
  items.forEach((item, index) => {
    const trimmed = String(item.category ?? '').trim();
    const rawCategory = PLACEHOLDER_CATEGORIES.has(trimmed.toLowerCase()) ? '' : trimmed;
    // Items with no category share a single implicit bucket that renders
    // without a heading. Real category labels ("Pricing", "Amenities", ...)
    // still surface as section titles.
    const isImplicit = rawCategory.length === 0;
    const key = isImplicit ? '__uncategorized__' : rawCategory;
    let group = groups.get(key);
    if (!group) {
      group = { category: isImplicit ? '' : rawCategory, isImplicit, items: [] };
      groups.set(key, group);
    }
    group.items.push({ item, index });
  });
  return Array.from(groups.values());
}

// ============================================================================
// Component
// ============================================================================

export function FAQList() {
  const items = useCollectionBinding<FAQItem>('faqlist.items', {
    collection: 'faqs',
    defaultLimit: 50,
  });

  const cta = useSlotGroup('faqlist.cta_link', ctaLinkDef);

  const groups = useMemo(() => groupByCategory(items || []), [items]);
  const hasFaqs = groups.length > 0;

  // Named categories only — empty/missing category values stay grouped but
  // never surface as a filter chip or section title.
  const namedCategories = useMemo(
    () => groups.filter((g) => !g.isImplicit).map((g) => g.category),
    [groups],
  );
  const hasNamedCategories = namedCategories.length > 0;
  const showCategoryFilter = namedCategories.length > 1;

  const [activeCategory, setActiveCategory] = useState<string>('all');

  const visibleGroups = useMemo(() => {
    if (!showCategoryFilter || activeCategory === 'all') return groups;
    return groups.filter(
      (g) => !g.isImplicit && g.category === activeCategory,
    );
  }, [groups, showCategoryFilter, activeCategory]);

  // Track expanded state by a stable per-item id — first item of the first
  // visible group opens by default so the page never renders as a wall of
  // closed rows. The item's collection index is unique across groups, so it
  // alone is a safe key even when a group has no category label.
  const firstVisible = visibleGroups[0];
  const firstKey =
    firstVisible && firstVisible.items.length > 0
      ? String(firstVisible.items[0].index)
      : null;
  const [openKey, setOpenKey] = useState<string | null>(firstKey);

  const handleCategoryChange = (key: string) => {
    setActiveCategory(key);
    setOpenKey(null);
  };

  return (
    <section className="py-12 md:py-20 bg-[var(--vibe-color-background)]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

        {hasFaqs ? (
          <EditableCollectionBinding bindingPath="faqlist.items" label="FAQs">
            <div className="flex flex-col gap-10 md:gap-14">
              {/* Category filter — only when Multihub has 2+ distinct FAQ
                  categories. Single-category pages just get the section heading. */}
              {showCategoryFilter ? (
                <nav
                  aria-label="FAQ categories"
                  className="faq-category-nav"
                >
                  <button
                    type="button"
                    className={`faq-category-chip${activeCategory === 'all' ? ' is-active' : ''}`}
                    onClick={() => handleCategoryChange('all')}
                  >
                    All
                  </button>
                  {namedCategories.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      className={`faq-category-chip${activeCategory === cat ? ' is-active' : ''}`}
                      onClick={() => handleCategoryChange(cat)}
                    >
                      {cat}
                    </button>
                  ))}
                </nav>
              ) : null}

              {visibleGroups.map((group, gi) => (
                <div key={group.isImplicit ? `__uncategorized__-${gi}` : group.category}>
                  {/* Show the category title whenever Multihub provided one.
                      Implicit (uncategorized) buckets stay heading-free so
                      properties without categories don't get a placeholder. */}
                  {hasNamedCategories && !group.isImplicit ? (
                    <h2 className="faq-category-heading font-heading text-xl md:text-2xl uppercase tracking-widest text-[var(--vibe-color-text)] mb-5 md:mb-6">
                      {group.category}
                      <span aria-hidden="true" className="faq-category-underline" />
                    </h2>
                  ) : null}

                  <ul className="flex flex-col gap-3 list-none p-0 m-0">
                    {group.items.map(({ item, index }) => {
                      const key = String(index);
                      const isOpen = openKey === key;
                      const question = (item.question || '').trim() || 'Question';
                      const answer = (item.answer || '').trim();
                      return (
                        <li
                          key={key}
                          className={`faq-row ${isOpen ? 'is-open' : ''}`}
                        >
                          <button
                            type="button"
                            aria-expanded={isOpen}
                            className="faq-question"
                            onClick={() =>
                              setOpenKey((current) => (current === key ? null : key))
                            }
                          >
                            <span className="faq-question-text">{question}</span>
                            <span aria-hidden="true" className="faq-chevron" />
                          </button>
                          <div className="faq-answer-wrap" hidden={!isOpen}>
                            {answer ? (
                              <div
                                className="faq-answer [&_p]:mb-3 last:[&_p]:mb-0 [&_a]:text-primary [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 last:[&_ul]:mb-0 [&_li]:mb-2 [&_strong]:text-[var(--vibe-color-text)] [&_ol]:list-decimal [&_ol]:pl-5"
                                dangerouslySetInnerHTML={{ __html: formatAnswer(answer) }}
                              />
                            ) : (
                              <p className="faq-answer text-[var(--vibe-color-text-muted)] italic">
                                Answer coming soon.
                              </p>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </EditableCollectionBinding>
        ) : (
          <EditableCollectionBinding bindingPath="faqlist.items" label="FAQs">
            <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
              <p className="font-body text-lg">
                Frequently asked questions will appear here once added.
              </p>
            </div>
          </EditableCollectionBinding>
        )}

        {/* Contact footer — editable prompt + CTA button. */}
        <div className="faq-footer mt-14 md:mt-20 text-center">
          <RichTextContent
            path="faqlist.footer_heading"
            defaultValue="<p>Still have questions?</p>"
            as="h3"
            className="font-heading text-2xl md:text-3xl uppercase tracking-wider text-[var(--vibe-color-text)] mb-3"
          />
          <RichTextContent
            path="faqlist.footer_text"
            defaultValue="<p>Our leasing team is here to help. Reach out and we'll get back to you shortly.</p>"
            as="div"
            className="font-body text-base leading-relaxed text-[var(--vibe-color-text-muted)] max-w-xl mx-auto mb-6 [&_a]:text-primary [&_a]:underline"
          />
          <EditableSlotGroup groupPath="faqlist.cta_link" definition={ctaLinkDef}>
            <a
              href={cta.url || '/contact-us'}
              target={cta._target || '_self'}
              className="faq-cta"
            >
              {cta.label || 'Contact Us'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        /* Category filter — only rendered when Multihub has 2+ named categories. */
        .faq-category-nav {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem 0.75rem;
          justify-content: center;
          margin-bottom: -0.5rem;
        }
        .faq-category-chip {
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--vibe-color-text-muted);
          background: transparent;
          border: none;
          border-bottom: 2px solid transparent;
          padding: 0.35rem 0.15rem;
          cursor: pointer;
          transition: color 0.2s ease, border-color 0.2s ease;
        }
        .faq-category-chip:hover {
          color: var(--vibe-color-text);
        }
        .faq-category-chip.is-active {
          color: var(--vibe-color-text);
          border-bottom-color: var(--vibe-color-primary);
        }
        .faq-category-chip:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: 2px;
        }

        /* Section heading underline — mirrors the small accent bars used
           elsewhere in the theme. */
        .faq-category-heading {
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
        }
        .faq-category-underline {
          display: inline-block;
          height: 2px;
          width: 2.5rem;
          background: var(--vibe-color-primary);
        }

        /* Row — quiet card that lifts slightly on hover / open. */
        .faq-row {
          background: #fff;
          border: 1px solid #e8e8ea;
          border-radius: 4px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
          transition: box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
        }
        .faq-row:hover {
          border-color: color-mix(in srgb, var(--vibe-color-primary) 40%, #e8e8ea);
          box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
        }
        .faq-row.is-open {
          border-color: color-mix(in srgb, var(--vibe-color-primary) 60%, #e8e8ea);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
        }

        /* Question button — full-width, keyboard focusable. */
        .faq-question {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          width: 100%;
          padding: 1.1rem 1.25rem;
          text-align: left;
          background: transparent;
          border: none;
          cursor: pointer;
          font-family: var(--vibe-font-heading);
          font-size: 1rem;
          font-weight: 500;
          color: var(--vibe-color-text);
          text-transform: none;
          letter-spacing: 0.01em;
        }
        @media (min-width: 768px) {
          .faq-question {
            padding: 1.25rem 1.5rem;
            font-size: 1.05rem;
          }
        }
        .faq-question:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: -2px;
        }
        .faq-question-text {
          flex: 1 1 auto;
          min-width: 0;
        }

        /* Chevron — pure CSS caret that rotates on open. */
        .faq-chevron {
          position: relative;
          flex: 0 0 auto;
          width: 12px;
          height: 12px;
          border-right: 2px solid var(--vibe-color-primary);
          border-bottom: 2px solid var(--vibe-color-primary);
          transform: rotate(45deg);
          transition: transform 0.25s ease;
          margin-top: -4px;
        }
        .faq-row.is-open .faq-chevron {
          transform: rotate(-135deg);
          margin-top: 4px;
        }

        /* Answer body — indented under the question. */
        .faq-answer-wrap {
          padding: 0 1.25rem 1.25rem;
          animation: faqAnswerIn 0.25s ease both;
        }
        @media (min-width: 768px) {
          .faq-answer-wrap {
            padding: 0 1.5rem 1.5rem;
          }
        }
        .faq-answer {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.7;
          color: var(--vibe-color-text-muted);
        }
        @keyframes faqAnswerIn {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .faq-answer-wrap { animation: none; }
          .faq-chevron { transition: none; }
          .faq-row { transition: none; }
        }

        /* Footer CTA — matches PetPolicy's outlined-button style. */
        .faq-cta {
          display: inline-block;
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: var(--vibe-color-text);
          background: transparent;
          border: 2px solid var(--vibe-color-primary);
          padding: 0.85rem 2.25rem;
          border-radius: 2px;
          text-decoration: none;
          transition: background 0.2s, color 0.2s;
        }
        .faq-cta:hover {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'FAQList',
  displayName: 'FAQ List',
  tokens: {
    'faqlist.footer_heading': {
      label: 'Footer Heading',
      type: 'richtext',
      defaultValue: '<p>Still have questions?</p>',
    },
    'faqlist.footer_text': {
      label: 'Footer Text',
      type: 'richtext',
      defaultValue:
        "<p>Our leasing team is here to help. Reach out and we'll get back to you shortly.</p>",
    },
  },
  slotGroups: {
    'faqlist.cta_link': ctaLinkDef,
  },
  collectionBindings: {
    'faqlist.items': {
      label: 'FAQ Items',
      collection: 'faqs',
      defaultLimit: 50,
      description:
        'FAQs displayed in the accordion. When items have categories, they are grouped under category headings; two or more distinct categories also unlock a filter bar above the list.',
    },
  },
};

(FAQList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FAQList;
